import { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SubmitButton } from '@/components/ui/app/submit-button';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import FormTokensHistoryExport, { FORM_ID } from '@/pages/tokens/index/_partials/forms/FormTokensHistoryExport';
import { useTranslation } from 'react-i18next';
import i18n, { getIntlLocaleTag } from '@/lib/i18n';

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString(getIntlLocaleTag());
    } catch {
        return '—';
    }
};

const formatTime = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleTimeString(getIntlLocaleTag());
    } catch {
        return '—';
    }
};

const exportToCsv = (transactions) => {
    if (!transactions || transactions.length === 0) {
        toast.info(i18n.t('tokens:aucuneTransactionAExporter'));
        return;
    }

    const BOM = '\uFEFF';
    const headers = [
        i18n.t('tokens:date'),
        i18n.t('tokens:heure'),
        i18n.t('tokens:description'),
        i18n.t('tokens:type'),
        i18n.t('tokens:responsable'),
        i18n.t('tokens:dossier'),
        i18n.t('tokens:montantJetons'),
    ];

    const escapeCell = (value) => {
        const str = String(value ?? '');
        if (str.includes(';') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
    };

    const rows = transactions.map((t) => {
        const isCredit = t?.type === 'credit';
        const amount = Number(t?.token_amount ?? 0);
        const signedAmount = isCredit ? amount : -amount;
        return [
            escapeCell(formatDate(t?.created_at)),
            escapeCell(formatTime(t?.created_at)),
            escapeCell(t?.message || (isCredit ? i18n.t('tokens:credit') : i18n.t('tokens:debit'))),
            isCredit ? i18n.t('tokens:credit') : i18n.t('tokens:debit'),
            escapeCell(t?.user_name ?? ''),
            escapeCell(t?.folder_name ?? ''),
            signedAmount > 0 ? `+${signedAmount}` : String(signedAmount),
        ].join(';');
    });

    const csv = BOM + [headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const today = new Date().toISOString().slice(0, 10);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${i18n.t('tokens:nomFichierExportHistorique')}-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success(i18n.t('tokens:historiqueExporteAvecSucces'));
};

export default function DialogTokensHistoryExport({ open, onClose, companyUniqId }) {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleExport = async (params) => {
        if (!companyUniqId) return;
        setIsSubmitting(true);
        try {
            const res = await horodatyApi.getCompanyTransactions(companyUniqId, params);
            const data = res?.data?.data ?? [];
            exportToCsv(data);
            onClose();
        } catch {
            toast.error(t('tokens:impossibleDExporterLHistorique'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="text-lg font-semibold text-foreground">{t('tokens:exporterLHistorique')}</DialogTitle>
                </DialogHeader>

                <FormTokensHistoryExport onSubmit={handleExport} isSubmitting={isSubmitting} />

                <DialogFooter className="gap-2">
                    <DialogClose asChild>
                        <Button variant="outline" color="neutral" disabled={isSubmitting}>
                            {t('tokens:annuler')}
                        </Button>
                    </DialogClose>
                    <SubmitButton form={FORM_ID} isSubmitting={isSubmitting} loadingText={t('tokens:export')} className="flex-1">
                        {t('tokens:exporter')}
                    </SubmitButton>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
